"use client";

import { AnimatePresence, motion } from "framer-motion";
import { BookOpen, MessageCircle, X } from "lucide-react";
import * as React from "react";
import { createPortal } from "react-dom";

export interface WordActionAnchor {
  x: number;
  y: number;
  top?: number;
  bottom?: number;
}

interface WordActionBarProps {
  word: string;
  position: WordActionAnchor;
  onAskChicky?: (word: string) => void;
  onOpenFlashcard: (word: string, position: WordActionAnchor) => void;
  onClose: () => void;
}

export function WordActionBar({
  word,
  position,
  onAskChicky,
  onOpenFlashcard,
  onClose,
}: WordActionBarProps) {
  const barRef = React.useRef<HTMLDivElement>(null);
  const [portalRoot, setPortalRoot] = React.useState<HTMLElement | null>(null);
  const [adjustedPosition, setAdjustedPosition] = React.useState<WordActionAnchor | null>(null);

  React.useEffect(() => {
    setPortalRoot(document.body);
  }, []);

  React.useLayoutEffect(() => {
    if (!barRef.current) return;

    const bar = barRef.current;
    const rect = bar.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportPadding = 16;
    const verticalGap = 20;
    const anchorTop = position.top ?? position.y;

    let x = position.x - rect.width / 2;
    const y = Math.max(viewportPadding, anchorTop - rect.height - verticalGap);

    if (x + rect.width > viewportWidth - viewportPadding) {
      x = viewportWidth - rect.width - viewportPadding;
    }
    if (x < viewportPadding) {
      x = viewportPadding;
    }

    setAdjustedPosition({ x, y });
  }, [portalRoot, position]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (barRef.current && !barRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  React.useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const handleAskChicky = () => {
    onAskChicky?.(word);
    onClose();
  };

  const handleOpenFlashcard = () => {
    onOpenFlashcard(word, position);
    onClose();
  };

  const selectedWords = word.trim().split(/\s+/).filter(Boolean);
  const displayWord = selectedWords.length > 6 ? `${selectedWords.slice(0, 6).join(" ")}...` : word;

  const actionBar = (
    <AnimatePresence>
      <motion.div
        ref={barRef}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.15, ease: "easeOut" }}
        className="fixed z-[100] flex max-w-[min(360px,calc(100vw-32px))] flex-col gap-2 overflow-hidden rounded-2xl p-2 shadow-2xl"
        style={{
          left: `${adjustedPosition?.x ?? 0}px`,
          top: `${adjustedPosition?.y ?? 0}px`,
          visibility: adjustedPosition ? "visible" : "hidden",
          background:
            "linear-gradient(180deg, rgba(42, 45, 66, 0.98) 0%, rgba(32, 36, 58, 0.98) 100%)",
          backdropFilter: "blur(18px)",
          border: "1px solid rgba(253, 149, 79, 0.36)",
          boxShadow:
            "0 18px 48px rgba(0, 0, 0, 0.42), 0 0 0 1px rgba(255, 255, 255, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.08)",
          transformOrigin: "50% 100%",
        }}
      >
        <div
          className="pointer-events-none absolute inset-x-3 top-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(253, 149, 79, 0.85) 50%, transparent 100%)",
          }}
        />

        <div className="flex items-center gap-1.5">
          {onAskChicky && (
            <button
              onClick={handleAskChicky}
              className="flex h-10 min-w-0 flex-1 items-center justify-center gap-2 rounded-xl border px-3 text-sm font-semibold shadow-lg transition-all duration-200 hover:bg-[#FD954F]/24 active:scale-[0.98]"
              style={{
                background: "rgba(253, 149, 79, 0.18)",
                borderColor: "rgba(253, 149, 79, 0.34)",
                boxShadow: "0 8px 18px rgba(253, 149, 79, 0.12)",
                color: "#FFD2B3",
              }}
            >
              <MessageCircle size={16} />
              <span>Ask</span>
            </button>
          )}

          <button
            onClick={handleOpenFlashcard}
            className="flex h-10 min-w-0 flex-1 items-center justify-center gap-2 rounded-xl border px-3 text-sm font-semibold shadow-lg transition-all duration-200 hover:bg-white/[0.08] hover:text-white active:scale-[0.98]"
            style={{
              background: "rgba(249, 249, 249, 0.06)",
              borderColor: "rgba(249, 249, 249, 0.14)",
              boxShadow: "0 8px 18px rgba(0, 0, 0, 0.12)",
              color: "#E8E8EA",
            }}
          >
            <BookOpen size={16} />
            <span>Flashcard</span>
          </button>

          <button
            onClick={onClose}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border transition-colors hover:bg-white/[0.08] hover:text-white"
            style={{
              background: "rgba(26, 29, 43, 0.46)",
              borderColor: "rgba(255, 255, 255, 0.08)",
              color: "#AEAEB2",
            }}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div
          className="max-w-full truncate rounded-xl px-3 py-2 text-center text-sm font-medium"
          style={{
            background: "rgba(26, 29, 43, 0.5)",
            border: "1px solid rgba(253, 149, 79, 0.16)",
            color: "#FFD2B3",
          }}
        >
          &ldquo;{displayWord}&rdquo;
        </div>
      </motion.div>
    </AnimatePresence>
  );

  return portalRoot ? createPortal(actionBar, portalRoot) : null;
}
